import { useState, useEffect, useCallback } from "react";
import { AdminCard, AdminCardHead } from "components/admin/ui";
import { adminGet, adminPut } from "lib/adminClient";
import SlideOptions from "components/admin/SlideOptions";
import SlideImages from "components/admin/SlideImages";

const ACCENTS = [
  ["#f7efc8", "Cream"],
  ["#ffffff", "White"],
  ["#ffd6e0", "Pink"],
  ["#9B7200", "Gold"],
];

const DEFAULT_BG = "linear-gradient(140deg,#003d2b 0%,#005C40 55%,#007a55 100%)";
const blankSlide = () => ({ image: null, headline: "", sub: "", badge: "", accent: "#f7efc8", bg: DEFAULT_BG });
const blankStat = () => ({ value: "", label: "" });

const inputCls =
  "w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green";

export default function SlidesPage() {
  const [slides, setSlides] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, st] = await Promise.all([adminGet("/admin/content/slides"), adminGet("/admin/content/stats")]);
      setSlides(Array.isArray(s) ? s : []);
      setStats(Array.isArray(st) ? st : []);
      setLoadFailed(false);
    } catch (e) {
      // The form would otherwise render empty and a subsequent Save would
      // overwrite the live content with []. Flag it so saving is blocked.
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updSlide = (i, field, value) => setSlides(slides.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));
  const patchSlide = (i, patch) => setSlides((arr) => arr.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const applyAnimationToAll = (animation) =>
    setSlides((arr) => arr.map((s) => ({ ...s, animation: animation === "fade" ? undefined : animation })));
  const moveSlide = (i, dir) =>
    setSlides((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const c = [...arr];
      [c[i], c[j]] = [c[j], c[i]];
      return c;
    });
  const updStat = (i, field, value) => setStats(stats.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)));

  const save = async () => {
    // Never let a failed load turn into a destructive save: the form would be
    // empty through no fault of the editor, and Save would wipe the live rows.
    if (loadFailed) {
      setError("Content didn't load, so saving now would erase it. Reload the page first.");
      return;
    }
    const cleanedSlides = slides
      .filter((s) => (s.headline || "").trim() || s.image || s.textFromPrevious)
      .map((s) => ({ ...s, bg: s.bg || DEFAULT_BG }));
    if (
      cleanedSlides.length === 0 &&
      !window.confirm(
        "Save with no hero slides?\n\nThe homepage slider will be blank until you add one."
      )
    ) {
      return;
    }

    setSaving(true);
    setStatus("");
    setError("");
    try {
      const cleanedStats = stats.filter((s) => (s.value || "").trim() || (s.label || "").trim());
      await adminPut("/admin/content/slides", cleanedSlides);
      await adminPut("/admin/content/stats", cleanedStats);
      setSlides(cleanedSlides);
      setStats(cleanedStats);
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn">
      {error && <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>}

      {loadFailed && (
        <div className="text-[13px] text-[#8a6d00] bg-gold/[0.12] border border-gold/30 rounded-lg px-4 py-3 mb-4">
          <strong>This page didn't load your saved content.</strong> The form below is empty
          because of that — it does <em>not</em> mean your slides were deleted. Saving is
          disabled so it can't overwrite them.{" "}
          <button
            onClick={load}
            className="underline bg-transparent border-none p-0 cursor-pointer text-[#8a6d00] font-semibold"
          >
            Try again
          </button>
        </div>
      )}

      {/* Hero slides */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title={`Hero Slides (${slides.length})`}
          action={
            <button
              onClick={() => setSlides([...slides, blankSlide()])}
              className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90"
            >
              + Add Slide
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-4">
          {slides.length === 0 && <div className="text-muted text-sm">No slides yet — click “Add Slide”.</div>}
          {slides.map((s, i) => {
            return (
              <div key={i} className="border border-line rounded-xl p-4 grid grid-cols-1 md:grid-cols-[300px_1fr_auto] gap-4">
                {/* Desktop + mobile photo uploads (shared picker) */}
                <SlideImages slide={s} variant="home" fallbackBg={s.bg} onChange={(patch) => patchSlide(i, patch)} onError={setError} />

                <div className="flex flex-col gap-2">
                  {s.textFromPrevious && (
                    <div className="text-[12px] text-muted bg-[#f6f8f7] border border-line rounded-lg px-3 py-2">
                      This slide shows the previous slide's text — only its photo changes. Untick
                      “Same text as previous slide” below to give it its own caption.
                    </div>
                  )}
                  {s.showText === false && !s.textFromPrevious && (
                    <div className="text-[12px] text-muted bg-[#f6f8f7] border border-line rounded-lg px-3 py-2">
                      Text is hidden on this slide — visitors see the photo only.
                    </div>
                  )}
                  <input value={s.badge || ""} onChange={(e) => updSlide(i, "badge", e.target.value)} placeholder="Badge (small label, e.g. Welcome to)" className={inputCls} disabled={!!s.textFromPrevious} />
                  <textarea value={s.headline || ""} onChange={(e) => updSlide(i, "headline", e.target.value)} placeholder="Headline (press Enter for a line break)" rows={2} className={`${inputCls} resize-y font-serif`} disabled={!!s.textFromPrevious} />
                  <textarea value={s.sub || ""} onChange={(e) => updSlide(i, "sub", e.target.value)} placeholder="Sub-text" rows={2} className={`${inputCls} resize-y`} disabled={!!s.textFromPrevious} />
                  <SlideOptions
                    slide={s}
                    index={i}
                    defaultDuration={5.5}
                    onChange={(patch) => patchSlide(i, patch)}
                    onApplyAnimationToAll={applyAnimationToAll}
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-[12.5px] text-muted">Accent:</span>
                    <select value={s.accent || "#f7efc8"} onChange={(e) => updSlide(i, "accent", e.target.value)} className={`${inputCls} bg-white max-w-[140px]`}>
                      {ACCENTS.map(([hex, name]) => (
                        <option key={hex} value={hex}>{name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex md:flex-col gap-1.5 shrink-0">
                  <button onClick={() => moveSlide(i, -1)} title="Move up" className="bg-[#f2f4f6] border-none rounded-md px-2 py-1 cursor-pointer text-xs">↑</button>
                  <button onClick={() => moveSlide(i, 1)} title="Move down" className="bg-[#f2f4f6] border-none rounded-md px-2 py-1 cursor-pointer text-xs">↓</button>
                  <button onClick={() => setSlides(slides.filter((_, idx) => idx !== i))} title="Remove" className="bg-[#fdeaea] border-none rounded-md px-2 py-1 cursor-pointer text-xs text-[#c44]">🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      </AdminCard>

      {/* Stat counters */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Stat Counters"
          action={
            <button
              onClick={() => setStats([...stats, blankStat()])}
              className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90"
            >
              + Add Stat
            </button>
          }
        />
        <div className="px-[22px] pt-3 hidden md:grid grid-cols-[1fr_2fr_auto] gap-3 text-[11px] font-bold text-[#9aa] uppercase tracking-[0.04em]">
          <span>Value</span><span>Label</span><span></span>
        </div>
        <div className="p-[22px] pt-2 flex flex-col gap-2">
          {stats.length === 0 && <div className="text-muted text-sm">No stats yet.</div>}
          {stats.map((s, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-3 items-center">
              <input value={s.value || ""} onChange={(e) => updStat(i, "value", e.target.value)} placeholder="4,200+" className={inputCls} />
              <input value={s.label || ""} onChange={(e) => updStat(i, "label", e.target.value)} placeholder="Students Enrolled" className={inputCls} />
              <button onClick={() => setStats(stats.filter((_, idx) => idx !== i))} title="Remove" className="bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44]">🗑</button>
            </div>
          ))}
        </div>
      </AdminCard>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="bg-green text-white border-none rounded-[9px] px-[26px] py-[11px] font-sans font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[13px] text-[#1a9f5e] font-semibold">{status}</span>}
      </div>
    </div>
  );
}
